/**
 * Helper para verificar permissões do usuário
 * @param {Object} user - Dados do usuário
 * @param {string} permissionKey - Chave da permissão
 * @returns {boolean} - Se o usuário tem a permissão
 */
export const can = (user, permissionKey) => {
  if (!user || !user.companies || user.companies.length === 0) {
    return false;
  }

  // Encontrar a empresa atual do usuário
  let currentCompany = user.companies.find(company => company.id === user.currentCompanyId);

  // Se não encontrou, tentar com a primeira empresa (fallback)
  if (!currentCompany && user.companies.length > 0) {
    currentCompany = user.companies[0];
  }

  if (!currentCompany || !currentCompany.role) {
    return false;
  }

  // Admin tem acesso total
  if (currentCompany.role.is_admin) {
    return true;
  }

  // Verificar se o role tem a permissão específica
  if (currentCompany.role.permissions) {
    return currentCompany.role.permissions.some(permission => permission.key === permissionKey);
  }

  return false;
};

/**
 * Verificar se o usuário é admin da empresa atual
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário é admin
 */
export const isAdmin = user => {
  if (!user || !user.companies || user.companies.length === 0) {
    return false;
  }

  // Encontrar a empresa atual do usuário
  let currentCompany = user.companies.find(company => company.id === user.currentCompanyId);

  // Se não encontrou, tentar com a primeira empresa (fallback)
  if (!currentCompany && user.companies.length > 0) {
    currentCompany = user.companies[0];
  }

  return currentCompany?.role?.is_admin || false;
};

/**
 * Verificar se o usuário pode gerenciar membros
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário pode gerenciar membros
 */
export const canManageMembers = user => {
  return can(user, 'member.manage') || can(user, 'member.update') || isAdmin(user);
};

/**
 * Verificar se o usuário pode gerenciar perfis
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário pode gerenciar perfis
 */
export const canManageRoles = user => {
  return (
    can(user, 'roles.manage') ||
    can(user, 'roles.update') ||
    can(user, 'roles.create') ||
    isAdmin(user)
  );
};

/**
 * Verificar se o usuário pode gerenciar áreas
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário pode gerenciar áreas
 */
export const canManageAreas = user => {
  return can(user, 'areas.manage') || can(user, 'areas.update') || isAdmin(user);
};

/**
 * Verificar se o usuário pode gerenciar empresa
 * @param {Object} user - Dados do usuário
 * @returns {boolean} - Se o usuário pode gerenciar empresa
 */
export const canManageCompany = user => {
  return (
    can(user, 'company.manage') ||
    can(user, 'company.update') ||
    can(user, 'company.create') ||
    isAdmin(user)
  );
};

/**
 * Helpers por módulo para Navegar/Criar/Editar/Excluir (CRUD)
 */
export const canNavigateModule = (user, moduleKey) => {
  return (
    isAdmin(user) ||
    can(user, `${moduleKey}.view`) ||
    can(user, `${moduleKey}.create`) ||
    can(user, `${moduleKey}.update`) ||
    can(user, `${moduleKey}.delete`)
  );
};

export const canCreateInModule = (user, moduleKey) => {
  return isAdmin(user) || can(user, `${moduleKey}.create`) || can(user, `${moduleKey}.update`);
};

export const canEditInModule = (user, moduleKey) => {
  return isAdmin(user) || can(user, `${moduleKey}.update`);
};

export const canDeleteInModule = (user, moduleKey) => {
  return isAdmin(user) || can(user, `${moduleKey}.delete`) || can(user, `${moduleKey}.update`);
};

/**
 * Obter todas as permissões do usuário para a empresa atual
 * @param {Object} user - Dados do usuário
 * @returns {Array} - Array de chaves de permissões
 */
export const getUserPermissions = user => {
  if (!user || !user.companies || user.companies.length === 0) {
    return [];
  }

  const currentCompany = user.companies.find(company => company.id === user.currentCompanyId);

  if (!currentCompany || !currentCompany.role) {
    return [];
  }

  // Admin tem todas as permissões
  if (currentCompany.role.is_admin) {
    return ['*']; // Wildcard para admin
  }

  // Retornar chaves das permissões do role
  if (currentCompany.role.permissions) {
    return currentCompany.role.permissions.map(permission => permission.key);
  }

  return [];
};

/**
 * Verificar se o usuário tem acesso a uma área específica
 * @param {Object} user - Dados do usuário
 * @param {string} areaSlug - Slug da área
 * @returns {boolean} - Se o usuário tem acesso à área
 */
export const hasAreaAccess = (user, areaSlug) => {
  if (!user || !user.companies || user.companies.length === 0) {
    return false;
  }

  const currentCompany = user.companies.find(company => company.id === user.currentCompanyId);

  if (!currentCompany || !currentCompany.role) {
    return false;
  }

  // Admin tem acesso a todas as áreas
  if (currentCompany.role.is_admin) {
    return true;
  }

  // Verificar se o role tem acesso à área
  if (currentCompany.role.areas) {
    return currentCompany.role.areas.some(area => area.slug === areaSlug);
  }

  return false;
};
